/*
 * PastQueries — drawer listing every query in this session. Clicking a row
 * fetches its trace + results + usage and renders a detail pane.
 *
 * Data source: GET /queries, /queries/{id}/trace, /queries/{id}/results,
 * /queries/{id}/usage. Reuses TracePanel for the reasoning view so trace
 * rendering stays single-sourced.
 */

import React from 'react';
import { getResults, getTrace, getUsage, listQueries } from '../api.js';
import { TracePanel } from './Trace.jsx';

function fmtElapsed(ms) {
  if (!ms || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtStarted(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}

function statusTone(status) {
  if (status === 'complete' || status === 'results') return 'var(--pos)';
  if (status === 'error') return 'var(--accent)';
  if (status === 'empty') return 'var(--warn)';
  if (status === 'streaming') return 'var(--accent)';
  return 'var(--ink-3)';
}

function QueryRow({ row, selected, onSelect }) {
  const apiCalls = row.usage?.api_calls ?? 0;
  const resultCount = row.result_count ?? 0;
  return (
    <button
      type="button"
      className={`pq-row mono${selected ? ' pq-row-selected' : ''}`}
      onClick={() => onSelect(row.query_id)}
    >
      <span className="pq-row-status" style={{ background: statusTone(row.status) }} />
      <span className="pq-row-q serif">"{row.query}"</span>
      <span className="pq-row-meta">
        <span className="num">{resultCount}</span>
        <span className="pq-row-dim"> result{resultCount === 1 ? '' : 's'}</span>
      </span>
      <span className="pq-row-meta">
        <span className="num">{apiCalls}</span>
        {row.usage?.budget > 0 ? (
          <span className="pq-row-dim">/{row.usage.budget} calls</span>
        ) : (
          <span className="pq-row-dim"> calls</span>
        )}
      </span>
      <span className="pq-row-meta pq-row-dim">{fmtElapsed(row.elapsed_ms)}</span>
      <span className="pq-row-meta pq-row-dim">{fmtStarted(row.started_at)}</span>
    </button>
  );
}

function UsageReadout({ usage }) {
  const calls = usage?.api_calls ?? 0;
  const budget = usage?.budget ?? 0;
  const input = usage?.input_tokens ?? 0;
  const output = usage?.output_tokens ?? 0;
  const overBudget = budget > 0 && calls >= budget;
  return (
    <div className="pq-usage mono">
      <div>
        <span className="pq-usage-label">API CALLS</span>
        <span className={`pq-usage-num num${overBudget ? ' pq-usage-over' : ''}`}>
          {calls}
          {budget > 0 && <span className="pq-row-dim"> / {budget}</span>}
        </span>
      </div>
      <div>
        <span className="pq-usage-label">INPUT TOKENS</span>
        <span className="pq-usage-num num">{input.toLocaleString()}</span>
      </div>
      <div>
        <span className="pq-usage-label">OUTPUT TOKENS</span>
        <span className="pq-usage-num num">{output.toLocaleString()}</span>
      </div>
    </div>
  );
}

function DetailPane({ queryId, summary, refreshKey }) {
  const [trace, setTrace] = React.useState([]);
  const [results, setResults] = React.useState([]);
  const [usage, setUsage] = React.useState({
    input_tokens: 0,
    output_tokens: 0,
    api_calls: 0,
  });
  const [loading, setLoading] = React.useState(true);
  const [errored, setErrored] = React.useState(false);

  React.useEffect(() => {
    if (!queryId) return undefined;
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    Promise.all([getTrace(queryId), getResults(queryId), getUsage(queryId)])
      .then(([t, r, u]) => {
        if (cancelled) return;
        setTrace(t.events || []);
        setResults(r.results || []);
        setUsage(u.usage || { input_tokens: 0, output_tokens: 0, api_calls: 0 });
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setErrored(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryId, refreshKey]);

  if (!queryId) {
    return (
      <div className="pq-detail-empty mono">
        select a past query to review its reasoning, results, and api usage
      </div>
    );
  }
  if (errored) {
    return <div className="pq-detail-empty mono">failed to load query — retry?</div>;
  }
  if (loading) {
    return <div className="pq-detail-empty mono">loading…</div>;
  }

  return (
    <div className="pq-detail">
      <header className="pq-detail-hd">
        <div>
          <div className="mono micro pq-row-dim">QUERY</div>
          <h3 className="pq-detail-q serif">"{summary?.query}"</h3>
        </div>
        <UsageReadout usage={usage} />
      </header>

      <section className="pq-section">
        <div className="mono micro pq-row-dim">REASONING & TOOL CALLS</div>
        <div className="pq-trace-shell">
          <TracePanel
            events={trace}
            status={summary?.status === 'complete' ? 'results' : summary?.status || 'results'}
            query={summary?.query || ''}
            elapsedMs={summary?.elapsed_ms || 0}
            collapsed={false}
            onToggle={() => {}}
          />
        </div>
      </section>

      <section className="pq-section">
        <div className="mono micro pq-row-dim">RESULTS · {results.length}</div>
        {results.length === 0 ? (
          <div className="pq-empty mono">no results returned</div>
        ) : (
          <ul className="pq-result-list">
            {results.map((r) => (
              <li key={r.id} className="pq-result">
                <span className="mono num">{r.timestamp}</span>
                <span className="pq-result-headline serif">{r.headline}</span>
                <span className="mono micro pq-row-dim">{r.clip_id}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export function PastQueriesDrawer({ open, onClose }) {
  const [rows, setRows] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [errored, setErrored] = React.useState(false);
  // Bumping refreshKey re-runs the load effect, which also re-mounts the
  // DetailPane via a fresh queryId-keyed render — covers the case where the
  // selected query is still streaming and the drawer holds a stale snapshot.
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    listQueries()
      .then((data) => {
        if (cancelled) return;
        const fetched = data.queries || [];
        setRows(fetched);
        setSelectedId((prev) => prev || (fetched[0]?.query_id ?? null));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setErrored(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, refreshKey]);

  if (!open) return null;
  const selected = rows.find((r) => r.query_id === selectedId);

  return (
    <div className="pq-overlay" role="dialog" aria-modal>
      <div className="pq-overlay-scrim" onClick={onClose} aria-hidden />
      <aside className="pq-panel">
        <header className="pq-hd">
          <div>
            <div className="mono micro pq-row-dim">PAST QUERIES</div>
            <h2 className="pq-hd-h serif">session history</h2>
          </div>
          <div className="pq-hd-actions">
            <button
              type="button"
              className="pq-close mono"
              onClick={() => setRefreshKey((k) => k + 1)}
              disabled={loading}
            >
              refresh
            </button>
            <button type="button" className="pq-close mono" onClick={onClose}>
              close
            </button>
          </div>
        </header>

        <div className="pq-body">
          <div className="pq-list">
            {loading && <div className="pq-empty mono">loading…</div>}
            {errored && <div className="pq-empty mono">failed to load history</div>}
            {!loading && !errored && rows.length === 0 && (
              <div className="pq-empty mono">no queries yet in this session</div>
            )}
            {!loading && !errored && rows.length > 0 && (
              <div className="pq-list-rows">
                {rows.map((row) => (
                  <QueryRow
                    key={row.query_id}
                    row={row}
                    selected={row.query_id === selectedId}
                    onSelect={setSelectedId}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="pq-detail-wrap">
            <DetailPane queryId={selectedId} summary={selected} refreshKey={refreshKey} />
          </div>
        </div>
      </aside>

      <style>{`
        .pq-overlay {
          position: fixed; inset: 0; z-index: 100;
          display: flex; justify-content: flex-end;
        }
        .pq-overlay-scrim {
          position: absolute; inset: 0; background: rgba(20, 18, 14, 0.45);
          backdrop-filter: blur(2px);
        }
        .pq-panel {
          position: relative; height: 100%; width: min(96vw, 1180px);
          background: var(--bg); border-left: 1px solid var(--rule);
          display: flex; flex-direction: column;
          animation: pq-slide .25s ease;
        }
        @keyframes pq-slide { from { transform: translateX(40px); opacity: 0; } }

        .pq-hd {
          display: flex; align-items: baseline; justify-content: space-between;
          padding: 22px 32px; border-bottom: 1px solid var(--rule);
        }
        .pq-hd-h { font-size: 22px; font-weight: 400; margin: 4px 0 0; }

        .pq-hd-actions { display: inline-flex; gap: 8px; }
        .pq-close {
          font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
          padding: 6px 14px; border: 1px solid var(--rule-2); background: transparent;
        }
        .pq-close:hover:not(:disabled) { color: var(--ink); border-color: var(--ink); }
        .pq-close:disabled { opacity: 0.5; }

        .pq-body {
          flex: 1; min-height: 0;
          display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
        }
        .pq-list { border-right: 1px solid var(--rule); overflow-y: auto; padding: 12px 0; }
        .pq-list-rows { display: flex; flex-direction: column; }
        .pq-empty { padding: 32px; color: var(--ink-3); font-size: 11px; letter-spacing: 0.06em; }

        .pq-row {
          display: grid;
          grid-template-columns: 6px minmax(0, 1.6fr) 90px 90px 60px 70px;
          gap: 12px; align-items: center;
          width: 100%; text-align: left;
          padding: 12px 24px; border: 0; border-bottom: 1px solid var(--rule);
          background: transparent; color: var(--ink-2); font-size: 12px;
          cursor: pointer;
        }
        .pq-row:hover { background: var(--panel); color: var(--ink); }
        .pq-row-selected { background: var(--panel-2); color: var(--ink); }
        .pq-row-status { width: 6px; height: 6px; border-radius: 99px; }
        .pq-row-q {
          font-size: 13px; color: var(--ink); font-style: italic; font-weight: 300;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .pq-row-meta { font-size: 10.5px; letter-spacing: 0.06em; white-space: nowrap; }
        .pq-row-dim { color: var(--ink-3); }

        .pq-detail-wrap { overflow-y: auto; }
        .pq-detail { padding: 24px 32px 40px; display: flex; flex-direction: column; gap: 22px; }
        .pq-detail-empty {
          height: 100%; display: grid; place-items: center;
          color: var(--ink-3); font-size: 11px; letter-spacing: 0.08em; padding: 32px;
        }
        .pq-detail-hd {
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; padding-bottom: 18px; border-bottom: 1px solid var(--rule);
        }
        .pq-detail-q { font-size: 24px; font-weight: 400; margin: 4px 0 0; color: var(--ink); }

        .pq-usage {
          display: flex; gap: 22px;
          font-size: 11px; color: var(--ink-3);
        }
        .pq-usage > div { display: flex; flex-direction: column; gap: 2px; }
        .pq-usage-label { letter-spacing: 0.14em; text-transform: uppercase; }
        .pq-usage-num { color: var(--ink); font-size: 17px; }
        .pq-usage-over { color: var(--accent); }

        .pq-section { display: flex; flex-direction: column; gap: 10px; }
        .pq-trace-shell {
          height: 320px; border: 1px solid var(--rule); overflow: hidden;
        }

        .pq-result-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
        .pq-result {
          display: grid; grid-template-columns: 80px minmax(0, 1fr) 220px;
          gap: 16px; padding: 10px 0; border-bottom: 1px solid var(--rule);
          font-size: 13px; color: var(--ink-2);
          align-items: baseline;
        }
        .pq-result-headline { color: var(--ink); }
        .micro { font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase; }
      `}</style>
    </div>
  );
}
